import { CommonModule, DatePipe } from "@angular/common";
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzCheckboxModule } from "ng-zorro-antd/checkbox";
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from "ng-zorro-antd/table";
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import * as uuid from 'uuid';
import { CustomTableColumn } from "../../../core/models/custom-table-fields";
import { ToasterService } from "../../../core/services/toaster-service";
import { UtilitiesService } from "../../../core/services/utilities.service";
import { MaterialModule } from "../../../core/shared/material.module";
import { AutocompleteInputComponent } from "../autocomplete-input/autocomplete-input.component";
import { DropdownWithSectionsComponent } from "../dropdown-with-sections/dropdown-with-sections.component";
import { MenuDropdownComponent } from "../menu-dropdown/menu-dropdown.component";
import { StarRankerComponent } from "../star-ranker/star-ranker.component";
import { VersionBadgeDropdownComponent } from "../../version-badge-dropdown/version-badge-dropdown.component";
import { MessageEnum } from "../../../core/utils/message.enum";

export interface Data {
  id: number;
  name: string;
  age: number;
  address: string;
  disabled: boolean;
}

@Component({
  selector: "app-zorro-table",
  standalone: true,
  imports: [
    NzButtonModule,
    NzTableModule,
    NzCheckboxModule,
    CommonModule,
    FormsModule,
    MaterialModule,
    DropdownWithSectionsComponent,
    NzSelectModule,
    NzToolTipModule,
    AutocompleteInputComponent,
    StarRankerComponent,
    MenuDropdownComponent,
    VersionBadgeDropdownComponent
  ],
  providers: [DatePipe],
  templateUrl: "./zorro-table.component.html",
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  styleUrl: "./zorro-table.component.scss",
})
export class ZorroTableComponent {
  @Input() columns: CustomTableColumn[] = [];
  @Input() data: any = [];
  @Input() meta: any;
  @Input() options: any = {};
  @Input() activeRowId: any;
  @Input() autocompleteOptions: any = {};
  @Input() isLoading: boolean = false;
  @Input() tableId: string = '';
  @Input() enableHorizontalScroll: boolean = false;
  /** 'fixed' is required for frozen columns (column.fixedLeft/fixedRight) to compute correct offsets. Defaults to 'auto' to preserve existing consumers' layout. */
  @Input() nzTableLayout: 'auto' | 'fixed' = 'auto';
  @Input() rightStikcy: any = {};
  @Input() hideVerticalBorder: boolean = false;
  @Input() borderColor: string = '';
  @Input() hideHeaders: boolean = false;
  @Input() hideTopBottomLines: boolean = false;
  @Input() whiteHeaderBg: boolean = false;
  @Output() handleActions = new EventEmitter<any>();
  @Output() onRowSelect = new EventEmitter<any>();
  @Output() requireForModelChanged = new EventEmitter<{ index: number, value: string }>();
  @Output() pageChange = new EventEmitter<{ page: number, size: number }>();
  @Output() filterActions = new EventEmitter<any>();
  @Output() testCaseFilterAction = new EventEmitter<any>();

  constructor(private readonly utils: UtilitiesService, private readonly toaster: ToasterService, private readonly datePipe: DatePipe) {}

  /**
   * Format a cell value as a date string.
   * @param value - raw date string from BE e.g. '2026-03-03 10:20:08.281000'
   * @param format - Angular DatePipe format string, defaults to 'MMMM dd, yyyy'
   */
  formatDateCell(value: any, format: string = 'MMMM dd, yyyy'): string {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : (this.datePipe.transform(date, format) ?? value);
  }

  isAllChecked = false;
  pageIndex: any = 1;
  isIndeterminate = false;
  selectedRows: any[] = [];
  browserDropdownOpen: { [rowId: string]: boolean } = {};
  defaultOptions: any = {
    pageSize: 5,
    showPagination: true,
    showPageSizeChanger: false,
  };
  mapOfExpandedData: { [key: string]: any[] } = {};
  color: string = "#ffffff";
  isSelectAllDisabled: boolean = false;
  rankSelection: any = {};

  getCols() {
    return this.columns;
  }

  /**
   * Check if single-select mode is enabled for checkbox
   */
  isSingleSelectMode(): boolean {
    const checkboxColumn = this.columns.find((col: any) => col._config?.type === 'checkbox');
    return checkboxColumn?._config?.singleSelect === true;
  }

  ngOnInit() {
    this.options = { ...this.defaultOptions, ...this.options };
    if(this.tableId && this.utils.getPageIndex(this.tableId)) {
      this.pageIndex = this.utils.getPageIndex(this.tableId);
    }
    this.initializeTable();
    if (this.options?.isSelectAll || this.meta?.isSelectAll) {
      const event = { target: { checked: true } };
      this.onSelectAll(event);
    }
  }

  updateOptions(options: any) {
    this.options = { ...this.defaultOptions, ...options };
    this.initializeTable();
  }

  initializeTable() {
    // Ensure `this.data` is an array
    this.data = Array.isArray(this.data) ? this.data : [];
    this.reOrderExpandableRows();
  }

  reOrderExpandableRows() {
    this.data.forEach((item: any) => {
      if (!item.id) {
        item.id = uuid.v4();
      }
      this.mapOfExpandedData[item.id] = this.convertTreeToList(item);
    });
  }

  refreshTableData() {
    this.initializeTable();
    this.disableSelectAll()

  }
  disableSelectAll() {
    if (this.data.length === 0) {
      this.isAllChecked = false;
      this.isSelectAllDisabled = true;
    } else {
      this.isSelectAllDisabled = false;
    }
  }
  convertTreeToList(root: any): any[] {
    const stack: any[] = [];
    const array: any[] = [];
    const hashMap = {};
    stack.push({
      ...root,
      level: 0,
      expand: root?.expand === true ? true : this.isAlreadyExpanded(root.id),
      id: root?.id ? root.id : uuid.v4()
    });


    while (stack.length !== 0) {
      const node = stack.pop()!;
      if (node.children) {
        for (let i = node.children.length - 1; i >= 0; i--) {
          stack.push({ ...node.children[i], level: node.level! + 1, expand: this.isAlreadyExpanded(root.id, node.children[i].id), parent: node, id: node.children[i].id ? node.children[i].id : uuid.v4() });
        }
      }
      this.visitNode(node, hashMap, array);
    }
    return array;
  }

  visitNode(node: any, hashMap: { [key: string]: boolean }, array: any[]): void {
    if (!hashMap[node.id]) {
      hashMap[node.id] = true;
      array.push(node);
    }
  }

  isAlreadyExpanded(id: string, childId?: string) {
    let isFound;
    if (childId) {
      isFound = this.mapOfExpandedData[id]?.find((ele: any) => ele.id === childId);
    } else {
      isFound = this.mapOfExpandedData[id]?.find((ele: any) => ele.id === id);
    }
    return isFound?.expand ? true : false;
  }

  collapse(array: any[], data: any, $event: boolean): void {
    data.expand = $event;
    if (data.children && !$event) {
      data.children.forEach((d: any) => {
        const target = array.find(a => a.id === d.id)!;
        if (target) {
          target.expand = $event;
        }
        this.collapse(array, target, $event);
      });
    }
  }

  selectAllRows() {
    this.isAllChecked = true;
    // this.isIndeterminate = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["data"]) {
      this.refreshTableData();
      // Sync header checkbox state with the incoming data's select state
      if (this.data && this.data.length > 0) {
        const allSelected = this.data.every((r: any) => r.select === true);
        const anySelected = this.data.some((r: any) => r.select === true);
        this.isAllChecked = allSelected;
        this.isIndeterminate = !allSelected && anySelected;
      } else {
        this.isAllChecked = false;
        this.isIndeterminate = false;
      }
    }
  }

  onSelectAll(event: any): void {
    this.isAllChecked = event.target.checked;
    this.isIndeterminate = false;
    //if deselect list is available 
    const deselected = this.meta?.deselectedRows || [];
   if(deselected.length > 0){
    this.data.forEach((record: any) => {
      const isDeselected = deselected.some(
        (row: any) => row.storyId === record.storyId
      );
      record.select = isDeselected ? false : event.target.checked;
    });
   }else{
    this.data.forEach((record: any) => (record.select = event.target.checked));
   }

    this.reOrderExpandableRows();
    this.onRowSelect.emit({
      type: "rowSelect",
      select_type: this.isAllChecked ? "all" : "",
      data: this.data,
      sortedOutData: this.data.filter((record: any) => record.select)
    });

  }

  onRowSelectChange(event: any, row: any): void {
    if(this.columns.some((ele: any) => ele.select)) {
      row.select = false;
      this.data[this.data.findIndex((item: any) => item.id == row.id)].select = false;
      this.reOrderExpandableRows();
      this.popToaster('Please deselect column selection to select rows.', false, false);
      return;
    }

    // Check if single-select mode is enabled
    const checkboxColumn = this.columns.find((col: any) => col._config?.type === 'checkbox');
    const isSingleSelectMode = checkboxColumn?._config?.singleSelect === true;

    if (isSingleSelectMode && event === true) {
      // Update this.data
      this.data.forEach((record: any) => {
        record.select = record.id === row.id;
      });
      // CRITICAL: also update mapOfExpandedData items directly,
      // because [(ngModel)] is bound to those copied objects, not this.data
      Object.keys(this.mapOfExpandedData).forEach((key) => {
        this.mapOfExpandedData[key].forEach((item: any) => {
          item.select = item.id === row.id;
        });
      });
    } else {
      let currentRow = this.data.filter((item: any) => item.id == row.id)[0];
      currentRow.select = event;
      row.select = event;
    }

    this.onRowSelect.emit({
      type: "rowSelect",
      data: this.data.filter((record: any) => record.select),
      select_type: this.isAllChecked ? "all" : "",
      deselectedData: this.data.filter((record: any) => record.select === false),
      sortedOutData: this.data.filter((record: any) => record.select),
    });
  }

  getSecondLineData(value: string) {
    const toSentenceCase = (s: string) =>
            s ? s.replace(/_/g, ' ').charAt(0).toUpperCase() + s.replace(/_/g, ' ').slice(1).toLowerCase() : s;
    if(this.tableId === 'device_list' && value?.toLocaleLowerCase() === 'android') {
      return toSentenceCase(value);
    } else {
      return value;
    }
  }

  onColumnSelectChange(event: any, column: CustomTableColumn) {
     if(this.data.some((ele: any) => ele.select)) {
      this.columns[this.columns.findIndex((item: any) => item.key == column.key)].select = false;
      this.reOrderExpandableRows();
      this.popToaster('Please deselect row selection to select columns.', false, false);
      return;
    }
    let currentRow = this.data.filter((item: any) => item.id == column.key)[0];
    currentRow.select = event;
    this.onRowSelect.emit({
      type: "rowSelect",
      data: this.columns.filter((record: any) => record.select),
      select_type: this.isAllChecked ? "all" : "",
      deselectedData: this.columns.filter((record: any) => record.select === false),
    });
  }

  /**
   * Displays a toaster notification.
   * @param message The message to display.
   * @param isSuccess Whether the notification indicates success.
   * @param showDefaultError Whether to display a default error message.
   */
  popToaster(message: string, isSuccess = true, showDefaultError?: boolean) {
    if (isSuccess) {
      this.toaster.pop({ type: "success", text: message });
    } else {
      this.toaster.pop({
        type: "error",
        text: showDefaultError ? MessageEnum.SOMETHING_WENT_WRONG : message,
      });
    }
  }


  getReadableDt(timestamp: any): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return timestamp;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  getNestedData(row: any, key: string) {
    if (!key || key === 'expand') return "";
    // Ensure key is a string
    const keyStr = typeof key === 'string' ? key : String(key);
    let res = keyStr.split(".").reduce((acc, part) => acc && acc[part], row) || "";
    if (!res) {
    }
    return res;
  }

  getString(data: any) {
    return JSON.stringify(data);
  }

  rowClick(data: any) {
    this.activeRowId = data.row.id;
    this.emitAction(data.columnConfig?.type, data);
  }

  emitAction(type: string, data: any, event?: any, actionType?: string, value?: any) {
    if (type === 'textarea' || type === 'input' || type === 'select') {
      this.updateValueInOriginal(data.row.id, data.column.key, data.row[data.column.key]);
    }
    if (data.column?.key === 'requireForModel') {
      this.toggleRequireForModel(data);
    }
    if (type === "textarea") {
      const updatedValue = (event.target as HTMLTextAreaElement).value;
      const updatedRow = { ...data.row };
      updatedRow[data.column.key] = updatedValue;
      data.row = updatedRow;
    }
    if (type === "thumb-feedback") {
      if (event == 1) {
        // Ensure feedbackEnabled is added or set to true
        if (!Object.prototype.hasOwnProperty.call(data.row, "feedbackEnabled")) {
          data.row = Object.assign({}, data.row, { feedbackEnabled: true });
          data.row = Object.assign({}, data.row, { iconClicked: 1 });
        } else {
          data.row.feedbackEnabled = true;
          data.row.iconClicked = 1;
        }
        //on thumbs down show text area

        const updatedValue = (event.target as HTMLTextAreaElement)?.value;
        const updatedRow = { ...data.row };
        updatedRow[data.column.key] = updatedValue;
        data.row = updatedRow;
      } else if (event == 0) {
        if (!Object.prototype.hasOwnProperty.call(data.row, 'feedbackEnabled')) {
          data.row = Object.assign({}, data.row, { feedbackEnabled: false });
          data.row = Object.assign({}, data.row, { iconClicked: 0 });
        } else {
          data.row.feedbackEnabled = false;
          data.row.iconClicked = 0;
        }
      }
    }

    if (type === 'menu_dropdown' || type === 'section_dropdown') {
      const chosen = value?.value || value?.id;
      // Update the cloned template-context row
      data.row[data.columnConfig.targetKey] = chosen;
      // Also sync back to the original row in this.data so saveAdHocPlan reads it
      const originalRow = this.data.find((r: any) => r.id === data.row.id);
      if (originalRow) {
        originalRow[data.columnConfig.targetKey] = chosen;
      }
    }
  
    this.handleActions.emit({
      type,
      data,
      actionType,
      value
    });
  }

  toggleBrowserDropdown(rowId: string, event: Event): void {
    event.stopPropagation();
    const current = this.browserDropdownOpen[rowId];
    this.browserDropdownOpen = {};
    this.browserDropdownOpen[rowId] = !current;
  }

  selectBrowserOption(row: any, key: string, value: string, event: Event): void {
    event.stopPropagation();
    // Update the cloned template-context row
    row[key] = value;
    // Also sync back to the original row in this.data so saveAdHocPlan reads it
    const originalRow = this.data.find((r: any) => r.id === row.id);
    if (originalRow) {
      originalRow[key] = value;
    }
    this.browserDropdownOpen = {};
    this.handleActions.emit({ type: 'browser-select', data: { row, column: { key } }, actionType: 'select', value });
  }

  @HostListener('document:click')
  closeAllBrowserDropdowns(): void {
    this.browserDropdownOpen = {};
  }

  toggleRequireForModel(changedData: any): void {

    const featureName = changedData.row.featureName;
    const newValue = changedData.row.requireForModel;
    if (featureName === 'Testcase Name' || featureName === 'Testcase Description') {
      const otherRow = this.data.find((row: any) =>
        (row.featureName === 'Testcase Name' && featureName !== 'Testcase Name') ||
        (row.featureName === 'Testcase Description' && featureName !== 'Testcase Description')
      );

      if (otherRow) {
        if (featureName === 'Testcase Name') {
          otherRow.requireForModel = newValue === 'Yes' ? 'No' : 'Yes';
        } else if (featureName === 'Testcase Description') {
          otherRow.requireForModel = newValue === 'Yes' ? 'No' : 'Yes';
        }
        const otherRowIndex = this.data.findIndex((row: any) => row.featureName === otherRow.featureName);

        this.requireForModelChanged.emit({
          index: otherRowIndex,
          value: otherRow.requireForModel
        });
      }
    }
    this.data = [...this.data];
  }

  getGroupClassName(list: any, classList?: any) {
    return classList ? classList : `col-md-${12 / list.length}`;
  }

  updateValueInOriginal(id: string, columnKey: string, value: string, arr: any[] = this.data) {
    let isResolved = false;
    arr.forEach((ele: any, ind: number) => {
      if (ele.id === id) {
        arr[ind][columnKey] = value;
        isResolved = true;
      }
      if (!isResolved && ele.children?.length) {
        this.updateValueInOriginal(id, columnKey, value, ele.children);
      }
    });
  }

  getSelectedLabels(values: string[] | string): string {
    if (!values) return '';
    const selected = this.data.columnConfig?.options?.filter((opt: any) =>
      Array.isArray(values) ? values.includes(opt.value) : opt.value === values
    );
    return selected?.map((opt: any) => opt.label).join(', ') || '';
  }

  passwordHold(data: any) {
    data.columnConfig.showPassword = true;
  }

  passwordRelease(data: any) {
    data.columnConfig.showPassword = false;
  }

  /** Visibility is tracked per row + column so one eye icon does not reveal other cells. */
  isCellPasswordVisible(data: any): boolean {
    return !!data?.row?.[this.cellPasswordKey(data)];
  }

  toggleCellPassword(data: any, event?: Event): void {
    event?.stopPropagation();
    if (!data?.row) {
      return;
    }
    const key = this.cellPasswordKey(data);
    data.row[key] = !data.row[key];
  }

  private cellPasswordKey(data: any): string {
    return `_showPassword_${data?.column?.key ?? 'value'}`;
  }

  onAutoCompleteSelect(value: any, type: any, data: any) {
    data.row[data.column.key] = value;
    this.emitAction(type, data);
  }

   onPageIndexChange(newPage: number): void {
    if(this.tableId) this.utils.setPageIndex(this.tableId, newPage);
    if(this.meta?.isSelectAll){
        const event = { target: { checked: true } };
        this.onSelectAll(event);
    }
    this.pageChange.emit({ page: newPage, size: this.meta?.pageSize });
  }

  onPageSizeChange(newSize: number): void {
    if(this.tableId) this.utils.setPageIndex(this.tableId, 1);
    this.pageChange.emit({ page: 1, size: newSize });
  }
  
  toggleFilterVisibility(column: CustomTableColumn) {
    column.displayFilter = !column.displayFilter;
  }

  applyRciFilter(column: CustomTableColumn) {
    let anySelected = false;
    Object.keys(this.rankSelection).forEach((key: any) => {
      if(this.rankSelection[key]) {
        anySelected = true;
      }
    });
    this.data.forEach((ele: any) => {
      if (anySelected && !this.rankSelection[ele.rciScore]) {
        ele.hideRow = true;
      } else {
        ele.hideRow = false;
      }
    });
    this.initializeTable();
    column.displayFilter = false;
    this.filterActions.emit({ type: 'rank' });
  }

  resetRciFilter() {
    this.data.forEach((ele: any) => {
      ele.hideRow = false;
    });
    this.rankSelection = {};
    this.initializeTable();
    this.filterActions.emit({ type: 'rank' });
  }

  /**
   * Updates the page size of the table and resets to the first page.
   * @param size The new page size.
   */
  public updatePageSize(size: number): void {
    this.options.pageSize = size;
    this.onPageIndexChange(1);
  }

  getPaginationData() {
    return this.data.filter((ele: any) => !ele.hideRow);
  }

  openColumnFilter(event:any){
    this.testCaseFilterAction.emit({ event });
  }

  /**
   * Status-chip suffix icon. Supports a single field match, or every condition
   * in `conditions`, plus an optional required object and URL on the row.
   */
  showLozengeSuffixIcon(data: any): boolean {
    return this.showConfiguredIcon(data?.columnConfig?.suffixActionIcon, data?.row);
  }

  showConfiguredIcon(config: any, row: any): boolean {
    if (!config || !row) {
      return false;
    }

    if (Array.isArray(config.conditions) && config.conditions.length) {
      const matches = config.conditions.every(
        (condition: { key: string; value: unknown }) => row[condition.key] === condition.value
      );
      if (!matches) {
        return false;
      }
    } else if (config.conditionalKey && row[config.conditionalKey] !== config.condition) {
      return false;
    }

    if (config.requireKey && !this.resolveRowPath(row, config.requireKey)) {
      return false;
    }

    if (config.urlKey && !this.resolveRowPath(row, config.urlKey)) {
      return false;
    }

    return true;
  }

  onAssetIconClick(event: Event, data: any): void {
    event.stopPropagation();
    this.emitAction("icon", data);
  }

  onLozengeSuffixClick(event: Event, data: any): void {
    event.stopPropagation();
    const config = data?.columnConfig?.suffixActionIcon;
    const url = config?.urlKey ? this.resolveRowPath(data?.row, config.urlKey) : null;
    if (typeof url === "string" && this.isHttpUrl(url)) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
    if (config?.actionType) {
      this.emitAction(config.actionType, data);
    }
  }

  private resolveRowPath(row: any, path: string): any {
    return path.split(".").reduce((current: any, key: string) => current?.[key], row);
  }

  private isHttpUrl(value: string): boolean {
    try {
      const url = new URL(value);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  }

}
